/**
 * Amplitude enrichment plugin — stamps the active `workspace_id` and `team_id`
 * onto every Amplitude event's `event_properties`.
 *
 * Heidi lets a user belong to multiple teams, so events must carry the team and
 * workspace they fired in to be attributable. It reads the same two live
 * sources the Snowplow `workspace_context` generator does — `getWorkspaceId()`
 * (workspace atom) and the active `teamId` from the analytics context registry
 * (set by setActiveTeam for the current workspace) — so a user spanning several
 * teams is attributed identically wherever the event lands.
 *
 * Running as an SDK enrichment plugin (rather than shaping the payload in the
 * `@heidi/runtime/analytics` pipeline) means EVERY Amplitude event is covered
 * with no per-call-site change — pipeline events routed through the adapter, the
 * SDK's own autocaptured events, and the direct `amplitude.track('$exposure', …)`
 * exposure calls — and the parity-locked `enrich()` stays untouched.
 *
 * Registration is gated by `isSaasTelemetryEnabled` at the call site
 * (`amplitude-provider.tsx`), so a suppressed / on-prem build never adds it
 * (F1 on-prem guardrail).
 */

import { type Types } from '@amplitude/analytics-browser';
import { getAnalyticsContext } from '@heidi/runtime/analytics';
import { getWorkspaceId } from '@heidi/workspace/data';

export const WORKSPACE_CONTEXT_PLUGIN_NAME = 'heidi-workspace-context-enrichment';

/**
 * Build the enrichment plugin. `execute` runs synchronously per event: it
 * resolves the active workspace/team and fills `workspace_id` + `team_id` into
 * `event_properties`.
 *
 * Fill-only: a value a call site already set is preserved (a few events carry an
 * event-specific `team_id`). Both keys are always present afterwards — set to
 * `null` when the id is unknown — so the Amplitude schema is consistent and
 * queryable rather than sometimes-absent.
 */
export function workspaceContextEnrichmentPlugin(): Types.EnrichmentPlugin {
  return {
    name: WORKSPACE_CONTEXT_PLUGIN_NAME,
    type: 'enrichment',
    setup: () => Promise.resolve(),
    // Non-async: the interface types execute as returning a Promise, but the work
    // is synchronous, so resolve directly rather than trip `require-await`.
    execute: (event) => {
      const workspace_id = getWorkspaceId() ?? null;
      const team_id = getAnalyticsContext().teamId ?? null;
      // event_properties may be a fixed-shape SDK type (e.g. RevenueEventProperties)
      // or absent; treat it as an open record we may add keys to. Mutate in place
      // rather than clone, so per-event work stays O(1), not O(props).
      const props = (event.event_properties ?? {}) as Record<string, unknown>;

      // Fill only when unset. A real value or an explicit null a call site set is
      // preserved (fill-only); `undefined` is treated as unset and replaced, so
      // both keys are always present and never left as undefined.
      if (props.workspace_id === undefined) props.workspace_id = workspace_id;
      if (props.team_id === undefined) props.team_id = team_id;

      event.event_properties = props;
      return Promise.resolve(event);
    },
  };
}
